import { useEffect, useState } from 'react';

// Current admin's identity + EFFECTIVE permissions (role defaults ± overrides),
// the source of truth for nav filtering and per-page access guards. Mirrors the
// useFeatureFlags pattern: in-memory cache shared across mounts, refetch on
// window focus so a super_admin's permission change propagates without reload.
let cached = null;
let promise = null;
const subs = new Set();
const EMPTY = { id: null, role: null, permissions: [], sites: [], siteRoles: {} };

const adminToken = () => localStorage.getItem('admin_token') || localStorage.getItem('adminToken');

function fetchMe() {
  const token = adminToken();
  if (!token) { cached = EMPTY; return Promise.resolve(cached); }
  promise = fetch('/api/admin/me', { headers: { Authorization: `Bearer ${token}` } })
    .then(r => (r.ok ? r.json() : EMPTY))
    .catch(() => EMPTY);
  return promise.then(m => { cached = m || EMPTY; for (const fn of subs) fn(cached); return cached; });
}

if (typeof window !== 'undefined') {
  let last = 0;
  window.addEventListener('focus', () => {
    if (Date.now() - last < 30_000) return;
    last = Date.now();
    if (adminToken()) fetchMe();
  });
}

// "View as role" preview (super_admin only) — mirrors server ADMIN_ROLES so a
// super_admin can validate the IA as a lesser role without a throwaway account.
const ROLE_PERMS = {
  admin: ['products', 'inventory', 'orders', 'users', 'analytics', 'marketing'],
  moderator: ['products', 'inventory', 'orders'],
  support: ['orders'],
};
let _viewAs = (typeof window !== 'undefined' && localStorage.getItem('admin_view_as')) || '';
const viewSubs = new Set();
function setViewAs(role) {
  _viewAs = role || '';
  try { role ? localStorage.setItem('admin_view_as', role) : localStorage.removeItem('admin_view_as'); } catch (_) {}
  for (const fn of viewSubs) fn(_viewAs);
}

export default function useAdminMe() {
  const [me, setMe] = useState(cached || EMPTY);
  const [viewAs, setVA] = useState(_viewAs);
  useEffect(() => {
    subs.add(setMe); viewSubs.add(setVA);
    if (cached) setMe(cached);
    else if (!promise) fetchMe();
    else promise.then(setMe);
    return () => { subs.delete(setMe); viewSubs.delete(setVA); };
  }, []);

  const isSuper = me.role === 'super_admin';
  // When a super_admin is previewing a role, evaluate can() against that role.
  const previewing = isSuper && viewAs && ROLE_PERMS[viewAs];
  const perms = me.permissions || [];
  const can = (perm) => {
    if (!perm) return true;
    if (previewing) return ROLE_PERMS[viewAs].includes(perm);
    return isSuper || perms.includes('all') || perms.includes(perm);
  };

  return { ...me, can, loaded: cached !== null, isSuper, viewAs, setViewAs };
}

// Force a refetch (e.g. after the current admin's own role/perms change).
useAdminMe.refresh = fetchMe;
